<!--
  CollectionProvider editor — reusable across NetworkSettings (any
  provider by id) and ServiceProviderSettings (own provider). See the
  companion TS file for the state machine and contract references.
-->

<!-- "No provider picked yet" placeholder — lets the host mount the
     editor eagerly without needing a *ngIf guard. -->
<div class="cp-editor-placeholder" *ngIf="providerId == null">
  <mat-icon>info_outline</mat-icon>
  <span i18n="@@Select a CollectionProvider to edit its settings.">Select a CollectionProvider to edit its settings.</span>
</div>

<ng-container *ngIf="providerId != null">

  <!-- LOADING -->
  <div class="cp-editor-loading" *ngIf="loadState === 'LOADING'">
    <mat-spinner diameter="24"></mat-spinner>
    <span i18n="@@Loading data">Loading data</span>
  </div>

  <!-- LOAD ERROR -->
  <div class="cp-editor-error" *ngIf="loadState === 'ERROR'">
    <mat-icon>error_outline</mat-icon>
    <span>{{loadError}}</span>
    <button mat-stroked-button color="primary" (click)="load()">
      <mat-icon>refresh</mat-icon><span i18n="@@Retry">Retry</span>
    </button>
  </div>

  <!-- LOADED -->
  <ng-container *ngIf="loadState === 'LOADED' && provider">

    <mat-card appearance="outlined" class="cp-editor-card">
      <mat-card-title>
        {{provider.Name || ('CollectionProvider #' + provider.ID)}}
        <span class="cp-editor-id">(#{{provider.ID}})</span>
      </mat-card-title>
      <mat-card-content>

        <!-- Header row: Name (read-only for now; renames happen through
             a dedicated admin path elsewhere in the app). -->
        <div class="cp-editor-name-row">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label i18n="@@Name">Name</mat-label>
            <input matInput [value]="provider.Name" [readonly]="true">
          </mat-form-field>
        </div>

        <!-- Known-settings section -->
        <h4 class="cp-editor-section-title" i18n="@@Payment settings">Payment settings</h4>
        <p class="cp-editor-section-help" i18n="@@cpKnownSettingsHelp">These values are read by the server's payment-routing layer. The server no longer falls back to environment variables — every value must be set here.</p>

        <!-- Legacy-key migration warnings (surfaces when the loaded
             blob still carries deprecated keys). Informational; the
             actual rename happens on load and the deprecated keys are
             dropped on save. -->
        <div class="cp-editor-migration-warnings" *ngIf="migrationWarnings.length > 0">
          <div class="cp-editor-migration-warning" *ngFor="let w of migrationWarnings">
            <mat-icon>warning_amber</mat-icon><span>{{w}}</span>
          </div>
        </div>

        <div class="cp-editor-known-grid">
          <ng-container *ngFor="let field of knownKeys">
            <div class="cp-editor-known-row" *ngIf="isKnownFieldVisible(field)">
              <!-- Enum field (mat-select). Used for PAYMENT_PROCESSOR. -->
              <mat-form-field appearance="outline" class="cp-editor-known-field" *ngIf="field.type === 'enum'">
                <mat-label>{{field.label}}</mat-label>
                <mat-select [(ngModel)]="knownValues[field.key]"
                            [disabled]="effectiveReadOnly"
                            [name]="'cpk_' + field.key">
                  <mat-option value="" i18n="@@(unset)">(unset)</mat-option>
                  <mat-option *ngFor="let opt of paymentProcessorOptions" [value]="opt.value">
                    {{opt.label}}
                  </mat-option>
                </mat-select>
                <mat-hint *ngIf="field.hint">{{field.hint}}</mat-hint>
              </mat-form-field>

              <!-- Text / password field (default).
                   Autocomplete / password-manager suppression: browsers
                   aggressively try to save (username, password) pairs
                   whenever they see a `type="password"` input near a
                   text input — even when the fields have nothing to do
                   with the site's own login (as with the CardPointe
                   API credentials configured here). To keep these out
                   of the browser's saved-logins prompt and out of
                   password-manager autofill (LastPass, 1Password,
                   Bitwarden, Dashlane), we (a) set `autocomplete` to
                   `'new-password'` on the credential rows via
                   `field.autocomplete` (WHATWG-honoured "this is a
                   NEW credential the user is establishing — don't
                   save or autofill from saved logins"), (b) attach
                   the well-known password-manager ignore attributes,
                   (c) set `data-form-type="other"` so 1Password's
                   heuristic backs off, and (d) use a generic,
                   non-suggestive `name` attribute (no "username" /
                   "password" tokens for browser autofill heuristics
                   to latch onto). The `type="password"` masking + eye
                   reveal toggle are preserved so the value is still
                   hidden by default. -->
              <mat-form-field appearance="outline" class="cp-editor-known-field" *ngIf="field.type !== 'enum'">
                <mat-label>{{field.label}}</mat-label>
                <input matInput
                       [type]="(field.sensitive && !revealed[field.key]) ? 'password' : 'text'"
                       [(ngModel)]="knownValues[field.key]"
                       [readonly]="effectiveReadOnly"
                       [name]="'cp-field-' + field.key.length"
                       [attr.autocomplete]="field.autocomplete || 'off'"
                       autocorrect="off"
                       autocapitalize="off"
                       spellcheck="false"
                       data-lpignore="true"
                       data-1p-ignore
                       data-bwignore
                       data-form-type="other">
                <button mat-icon-button matSuffix
                        *ngIf="field.sensitive"
                        type="button"
                        (click)="toggleReveal(field.key)"
                        i18n-title="@@Show/hide" title="Show/hide">
                  <mat-icon>{{revealed[field.key] ? 'visibility_off' : 'visibility'}}</mat-icon>
                </button>
                <mat-hint *ngIf="field.hint">{{field.hint}}</mat-hint>
              </mat-form-field>
            </div>
          </ng-container>
        </div>

        <!-- Custom-settings section -->
        <h4 class="cp-editor-section-title" i18n="@@Additional settings">Additional settings</h4>
        <p class="cp-editor-section-help" i18n="@@cpCustomSettingsHelp">Free-form key/value entries. Use for per-ServiceProvider overrides like PAYMENT_PROCESSOR_SP_&lt;id&gt; or PAYMENT_MERCHANT_ACCOUNT_SP_&lt;id&gt;, and any future settings the server begins to recognise.</p>

        <table class="cp-editor-custom-table" *ngIf="customRows.length > 0">
          <thead>
            <tr>
              <th i18n="@@Setting key">Setting key</th>
              <th i18n="@@Value">Value</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of customRows; let i = index">
              <td>
                <mat-form-field appearance="outline" class="full-width cp-editor-custom-field">
                  <input matInput [(ngModel)]="row.key" [readonly]="effectiveReadOnly"
                         [name]="'cpck_' + i" autocomplete="off"
                         i18n-placeholder="@@e.g. PAYMENT_PROCESSOR_SP_5" placeholder="e.g. PAYMENT_PROCESSOR_SP_5">
                </mat-form-field>
              </td>
              <td>
                <mat-form-field appearance="outline" class="full-width cp-editor-custom-field">
                  <input matInput [(ngModel)]="row.value" [readonly]="effectiveReadOnly"
                         [name]="'cpcv_' + i" autocomplete="off">
                </mat-form-field>
              </td>
              <td class="cp-editor-custom-actions">
                <button mat-icon-button color="warn" type="button"
                        [disabled]="effectiveReadOnly"
                        (click)="removeCustomRow(row)"
                        i18n-title="destructive removal|Button that permanently destroys the record. Must be clearly different from Cancel, which only closes a dialog.@@Delete" title="Delete">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="cp-editor-custom-actions-bar">
          <button mat-stroked-button color="primary" type="button"
                  [disabled]="effectiveReadOnly"
                  (click)="addCustomRow()">
            <mat-icon>add</mat-icon><span i18n="@@Add setting">Add setting</span>
          </button>
        </div>

        <!-- Save action + status -->
        <div class="cp-editor-actions">
          <button mat-raised-button color="primary" type="button"
                  [disabled]="effectiveReadOnly || saving"
                  (click)="save()">
            <mat-icon *ngIf="!saving">save</mat-icon>
            <mat-spinner *ngIf="saving" diameter="18"></mat-spinner>
            <span i18n="@@Save Payment Settings">Save Payment Settings</span>
          </button>
          <span class="cp-editor-save-flash" *ngIf="savedFlash">
            <mat-icon>check_circle</mat-icon><span i18n="@@Saved.">Saved.</span>
          </span>
          <span class="cp-editor-save-error" *ngIf="saveError">
            <mat-icon>error_outline</mat-icon><span>{{saveError}}</span>
          </span>
        </div>

      </mat-card-content>
    </mat-card>

  </ng-container>

</ng-container>

